import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import Cookies from 'js-cookie';
import { config } from '@Constants/rx-ui/constants';
import AppStorage from '@Services/AppStorage';
import { isBuildFeatureEnabled } from '@Utils/BuildFeatures/buildFeatures';
import { isAdobeTargetFeatureEnabled } from '@Services/AdobeTarget/AdobeTargetService';
import { Licenses } from '@Constants/CommonConstants';
import { RememberMeSettings } from '@Constants/rx-user/constants';
import { OT_GROUPS, onceOTGroupConsentGiven } from '@Services/OneTrustCookies';
import AuthUser from '@Services/Auth/AuthUser';
import { isIdleWarningActive } from '@Services/IdleWarningState';
import { paths, sourceKeyMapping } from '@AppRoutes/constants';
import history from '@AppRoutes/history';

const ID_PLUS_RX_STATE_KEY = 'idPlusRxState';

export const getAccessToken = (what) => {
  // eslint-disable-next-line no-param-reassign
  what = what || 'token';
  const cookie = Cookies.get(config.TOKEN_COOKIE) || '{}';
  return get(JSON.parse(cookie), what, null);
};

export const setAccessToken = (token) => {
  if (token) {
    const json = JSON.stringify({
      token,
      lastUse: Date.now(),
    });
    Cookies.set(config.TOKEN_COOKIE, json, { samesite: 'lax' });
  } else {
    Cookies.remove(config.TOKEN_COOKIE);
  }
};

export const _removeRxState = () => {
  AppStorage.instance.remove(ID_PLUS_RX_STATE_KEY);
};

export const isFeatureEnabled = (feature) => {
  // true - enabled, false - disabled in dev page, null - use the currentUser.details.features
  let featureEnabled =
    isBuildFeatureEnabled(feature) || isAdobeTargetFeatureEnabled(feature) || get(AuthUser.currentUser, `devOptions.${feature}`, null);

  if (featureEnabled === null) {
    const features = get(AuthUser.currentUser, 'details.features', '').split(';');
    featureEnabled = features.reduce((memo, f) => {
      if (!memo && f) {
        const parsed = f.split('=');
        return parsed[0] === feature && (parsed[1] || true);
      }
      return memo;
    }, null);
  }

  return featureEnabled;
};

export const isAnonymousUser = () => AuthUser.currentUser.details && AuthUser.currentUser.details.authType === 'anonymous';

export const isRMCLicense = () => {
  if (AuthUser.currentUser && AuthUser.currentUser.details) {
    return Licenses.ReaxysMedChem === AuthUser.currentUser.details.license;
  }
  return false;
};

export const isUpgradedFromAnonymous = () => get(AuthUser.currentUser, 'details.sessionUpgraded', false);

export const getUser = () => cloneDeep(AuthUser.currentUser);

export const isReaxysAllowed = () => get(AuthUser.currentUser, 'permissions', []).indexOf('RX2_APPLICATION') > -1;

export const getDevOptions = () => {
  const devOptions = get(AuthUser.currentUser, 'devOptions', {});
  return Object.entries(devOptions)
    .map((devOption) => {
      if (devOption[1]) {
        return devOption.join(':');
      }
      return null; // This removes any null values
    })
    .filter(Boolean)
    .join(',');
};

export const setLastActionTime = (time) => {
  // While the idle warning is open, only an explicit "Continue working" may extend the session,
  // so suppress every implicit writer (activity, websocket reconnect, interceptor). FR-018.
  if (isIdleWarningActive()) {
    return;
  }
  // eslint-disable-next-line no-param-reassign
  time = time || new Date().valueOf();
  AppStorage.instance.set('lastActionTime', time);
};

export const setRememberMeToken = (days, token) => {
  const date = new Date();
  const expires = new Date(date.setTime(date.getTime() + days * 86400000));
  if (token) {
    // OneTrust - set once Performance cookies consent given
    onceOTGroupConsentGiven(OT_GROUPS.FUNCTIONAL, () => {
      Cookies.set(RememberMeSettings.token, token, {
        expires,
        samesite: 'lax',
      });
    });
  } else {
    Cookies.remove(RememberMeSettings.token);
  }
};

export const goToLandingPage = (replaceHistory = false) => {
  const defaultForm = get(AuthUser.currentUser, 'details.settings.queryBuilderDefaultForm', null);
  const landingPage = get(AuthUser.currentUser, 'details.settings.landingPage');
  const landingPagePath = Object.keys(sourceKeyMapping).find((srcPath) => sourceKeyMapping[srcPath] === landingPage) || paths.SEARCH;

  AppStorage.instance.set('qbDefaultForm', defaultForm);

  if (replaceHistory) {
    history.replace({
      pathname: landingPagePath,
    });
  } else {
    history.push({
      pathname: landingPagePath,
    });
  }
};
